import { useEffect, useMemo, useState } from "react";
import { formatMoney } from "../../../features/utils/formatMoney";
import { useSelector } from "react-redux";
import { applicableFee } from "../../../utils/booking/changeFee";
import PassengerBreakdownCard, {
  applyAdjustment,
  defaultMarkupTargets,
} from "./PassengerBreakdownCard";

/** Reverse selling → airline when markup rule is known but airline bases were stripped. */
export function reverseAirlineAmount(selling, markup) {
  const sell = Math.max(0, Number(selling) || 0);
  const raw = Number(markup?.amount ?? markup?.markupValue);
  if (!Number.isFinite(raw) || raw <= 0) return sell;

  const variant = markup?.variant || markup?.markupVariant || "Markup";
  const type = markup?.type || markup?.markupType || "Fixed";

  if (type === "Percentage") {
    const pct = raw / 100;
    if (variant === "Markup") return sell / (1 + pct);
    if (variant === "Markdown" && pct < 1) return sell / (1 - pct);
    return sell;
  }

  // Fixed
  if (variant === "Markdown") return sell + raw;
  return Math.max(0, sell - raw);
}

function pickMarkup(row = {}, order = {}) {
  const variant =
    row?.markupVariant || order?.markupVariant || order?.markup_variant;
  const type = row?.markupType || order?.markupType || order?.markup_type;
  const amount =
    row?.markupValue ?? order?.markupValue ?? order?.markup_value ?? "";

  if (variant || type || amount !== "") {
    return {
      variant: variant || "Markup",
      type: type || "Fixed",
      amount: amount === "" || amount == null ? "" : amount,
    };
  }
  return null;
}

/**
 * Resolve airline base for one fee line.
 * Prefer explicit airline field → order root → reverse from markup → selling.
 */
export function resolveAirlineBase({
  row = {},
  order = {},
  airlineKey,
  rootKeys = [],
  sellingKey,
  markup,
}) {
  if (row?.[airlineKey] != null && row[airlineKey] !== "") {
    const n = Number(row[airlineKey]);
    if (Number.isFinite(n)) return Math.max(0, n);
  }

  for (const key of rootKeys) {
    if (order?.[key] != null && order[key] !== "" && Number(order[key]) !== 0) {
      const n = Number(order[key]);
      if (Number.isFinite(n)) return Math.max(0, n);
    }
  }

  const selling = Number(row?.[sellingKey] ?? order?.[sellingKey] ?? 0);
  if (markup && (Number(markup.amount) || 0) > 0) {
    return Math.round(reverseAirlineAmount(selling, markup) * 100) / 100;
  }

  return Math.max(0, selling);
}

export const PriceBreakdown = ({ data: defData, callback }) => {
  const [rows, setRows] = useState([]);
  const [markupByType, setMarkupByType] = useState({});
  const [markupTargetsByType, setMarkupTargetsByType] = useState({});
  const [lock, setLock] = useState(true);

  const {
    paymentSettings: { fees },
  } = useSelector((state) => state.paymentSettings);

  const travellerMap = useMemo(
    () => ({
      ADT: "ADT",
      Adult: "ADT",
      CHD: "CHD",
      CNN: "CHD",
      Child: "CHD",
      INS: "INF",
      INF: "INF",
      Infant: "INF",
    }),
    [],
  );

  const travellerLabel = {
    ADT: "Adult",
    CHD: "Child",
    INF: "Infant",
  };

  useEffect(() => {
    let cached = null;
    try {
      cached = JSON.parse(
        sessionStorage.getItem(`changeQuoteAdmin:${defData?._id}`) || "null",
      );
    } catch (_) {
      cached = null;
    }

    const orderWithCache = cached
      ? {
          ...defData,
          airlineChangePrice:
            defData?.airlineChangePrice || cached.airlineChangePrice,
          airlineFareDifference:
            defData?.airlineFareDifference || cached.airlineFareDifference,
          airlineChangePenalty:
            defData?.airlineChangePenalty || cached.airlineChangePenalty,
          fareDifferenceMarkup:
            defData?.fareDifferenceMarkup || cached.fareDifferenceMarkup,
          markupVariant: defData?.markupVariant || cached.markupVariant,
          markupType: defData?.markupType || cached.markupType,
          markupValue: defData?.markupValue || cached.markupValue,
        }
      : defData;

    const orderMarkup = pickMarkup({}, orderWithCache);

    const buildRow = (fromBreakdown, type, passengerId, cacheRow) => {
      const merged = { ...fromBreakdown, ...(cacheRow || {}) };
      const markup =
        pickMarkup(merged, orderWithCache) ||
        orderMarkup || {
          variant: "Markup",
          type: "Fixed",
          amount: "",
        };

      const sellingChange = Number(
        merged?.changePrice ?? orderWithCache?.changePrice ?? 0,
      );
      const sellingFare = Number(
        merged?.ticketDifference ?? orderWithCache?.ticketDifference ?? 0,
      );
      const sellingPenalty = Number(
        merged?.penaltyFee ?? orderWithCache?.penaltyFee ?? 0,
      );

      const airlineChangePrice = resolveAirlineBase({
        row: merged,
        order: orderWithCache,
        airlineKey: "airlineChangePrice",
        rootKeys: ["airlineChangePrice", "supplierChangePrice"],
        sellingKey: "changePrice",
      });

      const airlineFareDifference = resolveAirlineBase({
        row: merged,
        order: orderWithCache,
        airlineKey: "airlineFareDifference",
        rootKeys: ["airlineFareDifference", "airline_fare_difference"],
        sellingKey: "ticketDifference",
        markup,
      });

      const airlinePenaltyFee = resolveAirlineBase({
        row: merged,
        order: orderWithCache,
        airlineKey: "airlinePenaltyFee",
        rootKeys: ["airlinePenaltyFee", "airlineChangePenalty"],
        sellingKey: "penaltyFee",
      });

      const airlineTaxDifference = resolveAirlineBase({
        row: merged,
        order: orderWithCache,
        airlineKey: "airlineTaxDifference",
        rootKeys: ["airlineTaxDifference", "taxDifference"],
        sellingKey: "taxDifference",
        markup,
      });

      const airlineSupplierFee = resolveAirlineBase({
        row: merged,
        order: orderWithCache,
        airlineKey: "airlineSupplierFee",
        rootKeys: ["airlineSupplierFee", "supplierFee"],
        sellingKey: "supplierFee",
        markup,
      });

      const sellingTax = Number(merged?.taxDifference ?? airlineTaxDifference);
      const sellingSupplierFee = Number(
        merged?.supplierFee ?? airlineSupplierFee,
      );

      return {
        passengerId,
        type,
        airlineChangePrice,
        airlineFareDifference,
        airlinePenaltyFee,
        airlineTaxDifference,
        airlineSupplierFee,
        changePrice: sellingChange,
        ticketDifference: sellingFare,
        penaltyFee: sellingPenalty,
        taxDifference: sellingTax,
        supplierFee: sellingSupplierFee,
        fareDifferenceMarkup: Number(
          merged?.fareDifferenceMarkup ??
            orderWithCache?.fareDifferenceMarkup ??
            sellingFare - airlineFareDifference,
        ),
        taxDifferenceMarkup: Number(
          merged?.taxDifferenceMarkup ?? sellingTax - airlineTaxDifference,
        ),
        supplierFeeMarkup: Number(
          merged?.supplierFeeMarkup ?? sellingSupplierFee - airlineSupplierFee,
        ),
        markupVariant: markup.variant,
        markupType: markup.type,
        markupValue: markup.amount === "" ? 0 : Number(markup.amount) || 0,
        markupTargets: pickMarkupTargets(),
      };
    };

    function pickMarkupTargets() {
      return { ...defaultMarkupTargets };
    }

    if (defData?.priceBreakdown?.length) {
      const nextRows = defData.priceBreakdown.map((item, i) => {
        const { _id, ...rest } = item;
        const pax = (defData?.passengerInfo || []).find(
          (pas) => String(pas?.id) === String(item?.passengerId),
        );
        const type =
          pax?.travelerType ||
          rest?.type ||
          defData?.passengerInfo?.at(i)?.travelerType ||
          "ADT";
        const cacheRow = (cached?.priceBreakdown || []).find(
          (c) => String(c?.passengerId) === String(item?.passengerId),
        );
        return buildRow(
          rest,
          type,
          item?.passengerId || pax?.id || i + 1,
          cacheRow,
        );
      });
      setRows(nextRows);

      const nextMarkup = {};
      for (const r of nextRows) {
        const nType = travellerMap[r?.type] || r?.type || "ADT";
        if (nextMarkup[nType]) continue;
        if (r?.markupVariant || r?.markupType || Number(r?.markupValue) > 0) {
          nextMarkup[nType] = {
            variant: r.markupVariant || "Markup",
            type: r.markupType || "Fixed",
            amount: r.markupValue ?? "",
          };
        }
      }
      if (!Object.keys(nextMarkup).length && orderMarkup) {
        for (const r of nextRows) {
          const nType = travellerMap[r?.type] || r?.type || "ADT";
          nextMarkup[nType] = orderMarkup;
        }
      }
      setMarkupByType(nextMarkup);

      const nextTargets = {};
      for (const r of nextRows) {
        const nType = travellerMap[r?.type] || r?.type || "ADT";
        if (nextTargets[nType]) continue;
        nextTargets[nType] = { ...defaultMarkupTargets };
      }
      setMarkupTargetsByType(nextTargets);
    } else {
      const temp = (defData?.passengerInfo || []).map((obj, i) =>
        buildRow({}, obj?.travelerType, obj?.id || i + 1, null),
      );
      setRows(temp);
      setMarkupByType({});
      setMarkupTargetsByType({});
    }
    // eslint-disable-next-line
  }, [defData]);

  useEffect(() => {
    if (lock) setLock(false);
    else callback && callback(rows);
    // eslint-disable-next-line
  }, [rows]);

  const totalPayable = useMemo(() => {
    return (rows || []).reduce(
      (acc, cur) =>
        acc +
        Number(cur?.changePrice || 0) +
        Number(cur?.penaltyFee || 0) +
        Number(cur?.ticketDifference || 0),
      0,
    );
  }, [rows]);

  const fee =
    defData?.changeRequest === "PassengerInfo"
      ? fees?.changePassengerDetailFee
      : fees?.reIssueFee;

  const paxCount = rows?.length || 1;

  // Prefer saved order service fee when quoting again; else settings.
  const serviceChargeFee = useMemo(() => {
    const fromSettings = applicableFee(totalPayable, fee);
    const savedTotal = Number(
      defData?.intraverseServiceFee || defData?.changeFee || 0,
    );
    const savedPerPaxFromBreakdown = Number(
      defData?.priceBreakdown?.[0]?.changeFee || 0,
    );

    if (savedPerPaxFromBreakdown > 0) return savedPerPaxFromBreakdown;
    if (savedTotal > 0) return savedTotal / paxCount;
    return fromSettings;
  }, [
    totalPayable,
    fee,
    defData?.intraverseServiceFee,
    defData?.changeFee,
    defData?.priceBreakdown,
    paxCount,
  ]);

  const travellersByTypeCount = useMemo(() => {
    const map = {};
    for (const r of rows || []) {
      const normalized = travellerMap[r?.type] || r?.type || "ADT";
      map[normalized] = (map[normalized] || 0) + 1;
    }
    return map;
  }, [rows, travellerMap]);

  const uniqueTypes = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const r of rows || []) {
      const normalized = travellerMap[r?.type] || r?.type || "ADT";
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      out.push({ normalized, travelerType: normalized });
    }
    return out;
  }, [rows, travellerMap]);

  const isDetailsChange = defData?.changeRequest === "PassengerInfo";

  const getItemForType = (normalizedType) => {
    const anyRow = (rows || []).find(
      (r) => (travellerMap[r?.type] || r?.type || "ADT") === normalizedType,
    );

    const markup = markupByType[normalizedType] || {
      variant: "Markup",
      type: "Fixed",
      amount: "",
    };

    const markupTargets = { ...defaultMarkupTargets };

    // Details / name change: airline amount lives on fare difference (change
    // fee is always sent as 0) so markup still applies. Older quotes may
    // still have the amount on airlineChangePrice — prefer fare, fall back.
    if (isDetailsChange) {
      const fareAmt = anyRow
        ? Number(
            anyRow?.airlineFareDifference ||
              anyRow?.airlineChangePrice ||
              0,
          )
        : 0;
      return {
        travelerType: normalizedType,
        markup,
        markupTargets,
        breakDown: [
          { name: "Fare Difference", amount: fareAmt },
          {
            name: "Penalty",
            amount: Number(anyRow?.airlinePenaltyFee ?? 0),
          },
        ],
      };
    }

    if (!anyRow) {
      return {
        travelerType: normalizedType,
        markup,
        markupTargets,
        breakDown: [
          { name: "Change Fee", amount: 0 },
          { name: "Fare Difference", amount: 0 },
          { name: "Penalty", amount: 0 },
        ],
      };
    }

    return {
      travelerType: normalizedType,
      markup,
      markupTargets,
      breakDown: [
        {
          name: "Change Fee",
          amount: Number(anyRow?.airlineChangePrice ?? 0),
        },
        {
          name: "Fare Difference",
          amount: Number(anyRow?.airlineFareDifference ?? 0),
        },
        {
          name: "Penalty",
          amount: Number(anyRow?.airlinePenaltyFee ?? 0),
        },
      ],
    };
  };

  const handleSaveBreakdown = (
    normalizedType,
    nextBreakDown,
    nextMarkup,
    nextTargets,
  ) => {
    const bd = nextBreakDown || [];
    const getAmt = (label) =>
      Number(bd.find((x) => x?.name === label)?.amount || 0);

    // Details / name change: change fee always 0; entered amount → fare difference.
    const airlineChange = isDetailsChange
      ? 0
      : Math.max(0, getAmt("Change Fee"));
    const airlineFare = Math.max(0, getAmt("Fare Difference"));
    const airlinePenalty = Math.max(0, getAmt("Penalty"));

    const markup = nextMarkup || {
      variant: "Markup",
      type: "Fixed",
      amount: 0,
    };

    const targets = { ...defaultMarkupTargets };

    const adjChange = airlineChange;
    const adjFare = applyAdjustment(airlineFare, markup);
    const adjPenalty = airlinePenalty;

    setMarkupByType((prev) => ({ ...prev, [normalizedType]: markup }));
    setMarkupTargetsByType((prev) => ({
      ...prev,
      [normalizedType]: targets,
    }));

    setRows((prev) => {
      const next = [...(prev || [])];
      for (let i = 0; i < next.length; i++) {
        const nType = travellerMap[next[i]?.type] || next[i]?.type || "ADT";
        if (nType !== normalizedType) continue;

        const preservedTax = Number(next[i]?.taxDifference ?? 0);
        const preservedSupplier = Number(next[i]?.supplierFee ?? 0);
        const preservedAirlineTax = Number(next[i]?.airlineTaxDifference ?? 0);
        const preservedAirlineSupplier = Number(
          next[i]?.airlineSupplierFee ?? 0,
        );

        next[i] = {
          ...next[i],
          airlineChangePrice: airlineChange,
          airlineFareDifference: airlineFare,
          airlinePenaltyFee: airlinePenalty,
          changePrice: adjChange,
          ticketDifference: adjFare,
          penaltyFee: adjPenalty,
          taxDifference: preservedTax,
          supplierFee: preservedSupplier,
          airlineTaxDifference: preservedAirlineTax,
          airlineSupplierFee: preservedAirlineSupplier,
          fareDifferenceMarkup: adjFare - airlineFare,
          changePriceMarkup: 0,
          penaltyFeeMarkup: 0,
          taxDifferenceMarkup: 0,
          supplierFeeMarkup: 0,
          markupVariant: markup.variant,
          markupType: markup.type,
          markupValue: Number(markup.amount) || 0,
          markupTargets: targets,
          markupApplyChangeFee: false,
          markupApplyFareDifference: true,
          markupApplyPenalty: false,
          markupApplyTaxDifference: false,
          markupApplySupplierFee: false,
        };
      }
      return next;
    });
  };

  return (
    <div className="w-full flex flex-col gap-2">
      {defData?.supplierChangePrice ? (
        <div className="flex items-center justify-between rounded-lg bg-[#F8FAFC] border border-[#E8EEF5] px-3 py-2">
          <div className="text-xs text-[#6D7C94]">
            Original supplier change price
          </div>
          <div className="text-sm font-semibold text-[#0F172A]">
            {formatMoney(defData?.supplierChangePrice)}
          </div>
        </div>
      ) : null}

      <div className="space-y-2">
        {uniqueTypes.map(({ normalized }) => {
          const passengerCount = travellersByTypeCount[normalized] || 1;
          const item = getItemForType(normalized);

          return (
            <PassengerBreakdownCard
              key={normalized}
              normalized={normalized}
              serviceFee={serviceChargeFee}
              passengerCount={passengerCount}
              item={item}
              travelerType={normalized}
              travellerLabel={travellerLabel}
              handleSaveBreakdown={handleSaveBreakdown}
              isDetailsChange={isDetailsChange}
            />
          );
        })}
      </div>

      {/* Order-level service fee transparency */}
      <div className="rounded-lg border border-[#FEF3C7] bg-[#FFFBEB] px-3 py-2 flex items-center justify-between gap-2">
        <div>
          <div className="text-xs font-semibold text-[#92400E]">
            Intraverse service fee
          </div>
          <div className="text-[10px] text-[#B45309]">
            From fee config · added on top of final quote lines (manual has no
            markup rules)
          </div>
        </div>
        <div className="text-sm font-semibold text-[#92400E]">
          {formatMoney(serviceChargeFee * paxCount)}
        </div>
      </div>
    </div>
  );
};
